/**
 * Le impostazioni degli allegati.
 *
 * Prima il limite viveva in un file `.env` sul server: cambiarlo richiedeva
 * qualcuno che entrasse nella macchina e riavviasse l'applicazione. Da qui si
 * regola e vale dal caricamento successivo, senza riavviare niente.
 *
 * La pagina è divisa in due, e la divisione è voluta: sopra **le regole** —
 * cosa si può caricare — sotto **lo stato**, cioè quanto spazio si sta
 * occupando davvero. La seconda non è un ornamento: gli allegati stanno su
 * disco, e un disco che si riempie non si annuncia da solo.
 */
import { useEffect, useState } from 'react';

import { ErroreApi } from '../../api/client.ts';
import { impostazioniAllegati as api } from '../../api/scadenziario.ts';
import type { ImpostazioniAllegati, StatoSpazio } from '../../api/tipi-dati.ts';
import { istante } from '../../componenti/formato.ts';

export function Allegati() {
  const [imp, setImp] = useState<ImpostazioniAllegati | null>(null);
  const [spazio, setSpazio] = useState<StatoSpazio | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    let annullato = false;

    Promise.all([api.leggi(), api.spazio()])
      .then(([i, s]) => { if (!annullato) { setImp(i); setSpazio(s); } })
      .catch((e: unknown) => {
        if (!annullato) setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
      });

    return () => { annullato = true; };
  }, []);

  if (errore !== null) return <div className="avviso-errore" role="alert">{errore}</div>;
  if (imp === null) return <p className="stato-caricamento">Caricamento…</p>;

  return (
    <>
      <FormAllegati impostazioni={imp} onSalvate={setImp} />
      {spazio !== null && <Spazio stato={spazio} onCambiato={setSpazio} />}
    </>
  );
}

function FormAllegati({
  impostazioni,
  onSalvate,
}: {
  impostazioni: ImpostazioniAllegati;
  onSalvate: (i: ImpostazioniAllegati) => void;
}) {
  const [maxMb, setMaxMb] = useState(impostazioni.maxMb);
  const [formati, setFormati] = useState<string[]>(
    impostazioni.formati.filter((f) => f.ammesso).map((f) => f.chiave),
  );
  const [maxPerScadenza, setMaxPerScadenza] = useState(impostazioni.maxPerScadenza);
  const [soloAdmin, setSoloAdmin] = useState(impostazioni.soloAmministratoriEliminano);
  const [sogliaSpazioMb, setSogliaSpazioMb] = useState(impostazioni.sogliaSpazioMb);

  const [inSalvataggio, setInSalvataggio] = useState(false);
  const [conferma, setConferma] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  function commuta(chiave: string) {
    setFormati((attuali) =>
      attuali.includes(chiave) ? attuali.filter((c) => c !== chiave) : [...attuali, chiave],
    );
  }

  async function salva() {
    setErrore(null);
    setConferma(null);
    setInSalvataggio(true);

    try {
      const salvate = await api.salva({
        maxMb,
        formati,
        maxPerScadenza,
        soloAmministratoriEliminano: soloAdmin,
        sogliaSpazioMb,
      });
      onSalvate(salvate);
      setConferma('Impostazioni salvate. Valgono dal prossimo caricamento.');
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    } finally {
      setInSalvataggio(false);
    }
  }

  return (
    <>
      <h2 className="sezione-titolo">Cosa si può caricare</h2>

      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}
      {conferma !== null && <div className="avviso-esito" role="status">{conferma}</div>}

      <form className="form-impostazioni" onSubmit={(e) => { e.preventDefault(); void salva(); }}>
        <div className="campo">
          <label htmlFor="a-max">Dimensione massima di un file</label>
          <div className="campo-con-unita">
            <input
              id="a-max"
              type="number"
              min={1}
              max={impostazioni.massimoConsentitoMb}
              value={maxMb}
              onChange={(e) => setMaxMb(Number(e.target.value))}
            />
            <span className="unita">MB</span>
          </div>
          {/*
            Il tetto del server si dichiara qui, non solo nel messaggio
            d'errore: chi scrive 200 e si vede rifiutare senza saperlo prima
            pensa che l'applicazione sia rotta.
          */}
          <p className="aiuto">
            Al massimo {impostazioni.massimoConsentitoMb} MB: è il limite del server su cui gira
            l'applicazione, e oltre quello la richiesta verrebbe interrotta prima di arrivare
            qui. Per alzarlo serve una modifica alla configurazione del server.
          </p>
        </div>

        {/* Fuori da `.campo` per lo stesso motivo del selettore dei
            destinatari: `.campo label` vincerebbe sul testo delle spunte, e
            i nomi dei formati uscirebbero in maiuscoletto da 10 px. */}
        <div className="gruppo-spunte">
          <span className="etichetta-gruppo">Formati ammessi</span>
          <div className="spunte-formati">
            {impostazioni.formati.map((f) => (
              <label key={f.chiave} className="spunta">
                <input
                  type="checkbox"
                  checked={formati.includes(f.chiave)}
                  onChange={() => commuta(f.chiave)}
                />
                {f.nome}
              </label>
            ))}
          </div>
          <p className="aiuto">
            Il tipo viene riconosciuto dal contenuto del file, non dal suo nome: un file
            rinominato in «.pdf» viene rifiutato lo stesso.
            {formati.length === 0 && (
              <>
                {' '}
                <strong>
                  Senza nessuna spunta restano ammessi tutti i formati: per impedire i
                  caricamenti non si toglie tutto, si tolgono i permessi.
                </strong>
              </>
            )}
          </p>
        </div>

        <div className="campo">
          <label htmlFor="a-numero">Quanti allegati al massimo su una scadenza</label>
          <div className="campo-con-unita">
            <input
              id="a-numero"
              type="number"
              min={0}
              max={999}
              value={maxPerScadenza}
              onChange={(e) => setMaxPerScadenza(Number(e.target.value))}
            />
            <span className="unita">{maxPerScadenza === 0 ? 'nessun limite' : 'file'}</span>
          </div>
          <p className="aiuto">
            Lasciando <strong>0</strong> non c'è nessun limite. Non è una questione di spazio:
            una scadenza con quaranta file non si consulta più.
          </p>
        </div>

        <div className="gruppo-spunte">
          <label className="spunta">
            <input
              type="checkbox"
              checked={soloAdmin}
              onChange={(e) => setSoloAdmin(e.target.checked)}
            />
            Solo gli amministratori e i redattori possono eliminare un allegato
          </label>
          <p className="aiuto">
            Se resta spento può eliminarli chiunque possa modificare la scadenza. In ogni caso
            l'operazione resta scritta nel registro delle attività, con il nome del file e di
            chi l'ha tolto.
          </p>
        </div>

        <div className="campo">
          <label htmlFor="a-soglia">Avvisa quando lo spazio occupato supera</label>
          <div className="campo-con-unita">
            <input
              id="a-soglia"
              type="number"
              min={0}
              value={sogliaSpazioMb}
              onChange={(e) => setSogliaSpazioMb(Number(e.target.value))}
            />
            <span className="unita">{sogliaSpazioMb === 0 ? 'nessun avviso' : 'MB'}</span>
          </div>
          <p className="aiuto">
            Con <strong>0</strong> non si segnala niente. La soglia <strong>non blocca</strong> i
            caricamenti: un disco che si riempie va annunciato, ma fermare i certificati per un
            numero deciso a occhio sarebbe peggio del problema che risolve.
          </p>
        </div>

        <div className="dialogo-azioni">
          <button type="submit" className="bottone bottone-principale" disabled={inSalvataggio}>
            {inSalvataggio ? 'Salvataggio…' : 'Salva le impostazioni'}
          </button>
        </div>

        <p className="aiuto tracciabilita">
          Ultima modifica: {istante(impostazioni.modificataIl)}
        </p>
      </form>
    </>
  );
}

function Spazio({
  stato,
  onCambiato,
}: {
  stato: StatoSpazio;
  onCambiato: (s: StatoSpazio) => void;
}) {
  const [inPulizia, setInPulizia] = useState(false);
  const [esito, setEsito] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  async function pulisci() {
    setErrore(null);
    setEsito(null);
    setInPulizia(true);

    try {
      const r = await api.pulisciOrfani();
      setEsito(
        r.rimossi === 0
          ? 'Non c’era niente da togliere.'
          : `Tolti ${r.rimossi} file (${r.leggibile}).`,
      );
      onCambiato(await api.spazio());
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    } finally {
      setInPulizia(false);
    }
  }

  return (
    <section className="spazio-allegati">
      <h2 className="sezione-titolo" style={{ marginTop: 28 }}>Spazio occupato</h2>

      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}
      {esito !== null && <div className="avviso-esito" role="status">{esito}</div>}

      {stato.oltreLaSoglia && (
        <div className="avviso-attenzione" role="note">
          <strong>Gli allegati occupano più della soglia impostata.</strong>
          <p className="aiuto">
            Non è un blocco e non si è perso niente: è il momento di controllare quanto spazio
            resta sul disco del server, prima che se ne accorga il database.
          </p>
        </div>
      )}

      <dl className="dettaglio-scheda">
        <dt>File registrati</dt>
        <dd>
          <strong>{stato.quanti}</strong>
          <span className="tenue"> · {stato.leggibile}</span>
        </dd>

        <dt>Soglia d'avviso</dt>
        <dd>
          {stato.sogliaMb === 0
            ? <span className="tenue">nessuna</span>
            : `${stato.sogliaMb} MB`}
        </dd>

        <dt>File orfani</dt>
        <dd>
          {stato.orfani === 0 ? (
            <span className="tenue">nessuno</span>
          ) : (
            <>
              <strong>{stato.orfani}</strong>
              <span className="tenue"> · {stato.leggibileOrfani}</span>
            </>
          )}
        </dd>
      </dl>

      <p className="aiuto" style={{ maxWidth: '70ch' }}>
        Un file orfano è rimasto sul disco senza che nessuna scadenza lo nomini — succede se
        un caricamento si interrompe a metà. Non fa danno e non è visibile da nessuna parte:
        occupa spazio e basta. La pulizia confronta la cartella con il database e toglie
        <strong> solo ciò che nessuna riga nomina</strong>, mai per data o per dimensione.
      </p>

      <div className="dialogo-azioni">
        <button
          type="button"
          className="bottone"
          onClick={() => void pulisci()}
          disabled={inPulizia || stato.orfani === 0}
        >
          {inPulizia ? 'Pulizia…' : 'Togli i file orfani'}
        </button>
      </div>
    </section>
  );
}
